import React from 'react';
import { Typography, Collapse } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import StepDetailPanel from './StepDetailPanel';
import { ONBOARDING_STEPS } from './utils/constants';
import { CAT_YELLOW, CAT_BLACK } from './utils/styles';

interface OnboardingSectionProps {
  classes: any;
  onboardingRef: React.RefObject<HTMLDivElement>;
  showOnboarding: boolean;
  activeStep: number | null;
  setActiveStep: React.Dispatch<React.SetStateAction<number | null>>;
  stepRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
}

const CIRCLE = 48;

export default function OnboardingSection({
  classes,
  onboardingRef,
  showOnboarding,
  activeStep,
  setActiveStep,
  stepRefs,
}: OnboardingSectionProps) {
  const handleStepClick = (id: number) => {
    setActiveStep(prev => (prev === id ? null : id));
  };

  return (
    <div ref={onboardingRef}>
        <div className={classes.section} style={{ paddingTop: 48 }}>
          <Typography className={classes.sectionLabel}>Onboarding Process</Typography>
          <Typography className={classes.sectionTitle}>5-Step Guided Journey</Typography>
          <Typography className={classes.sectionSubtitle}>
            {showOnboarding
              ? 'Click a step in the wizard to view its details below.'
              : 'Click "Start Onboarding" above to begin, or select any step in the wizard below.'}
          </Typography>

          {/* Horizontal wizard stepper */}
          <div style={{ display: 'flex', alignItems: 'flex-start', margin: '36px 0 28px', overflowX: 'auto' }}>
            {ONBOARDING_STEPS.map((step, idx) => {
              const isActive = activeStep === step.id;
              const isCompleted = activeStep !== null && step.id < activeStep;
              const reached = isActive || isCompleted;
              return (
                <div
                  key={step.id}
                  ref={el => { stepRefs.current[step.id - 1] = el; }}
                  onClick={() => handleStepClick(step.id)}
                  role="button"
                  tabIndex={0}
                  aria-current={isActive ? 'step' : undefined}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleStepClick(step.id);
                    }
                  }}
                  style={{
                    flex: 1,
                    minWidth: 120,
                    position: 'relative',
                    textAlign: 'center',
                    cursor: 'pointer',
                    padding: '0 4px',
                  }}
                >
                  {/* Connector to previous node */}
                  {idx > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: CIRCLE / 2 - 1.5,
                      right: '50%',
                      width: '100%',
                      height: 3,
                      background: reached ? CAT_YELLOW : '#E0E0E0',
                      zIndex: 0,
                      transition: 'background 0.25s',
                    }} />
                  )}

                  {/* Circle node */}
                  <div style={{
                    position: 'relative',
                    zIndex: 1,
                    width: CIRCLE,
                    height: CIRCLE,
                    borderRadius: '50%',
                    margin: '0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: reached ? CAT_YELLOW : '#FFFFFF',
                    border: reached ? `2px solid ${CAT_YELLOW}` : '2px solid #E0E0E0',
                    color: reached ? CAT_BLACK : '#9E9E9E',
                    boxShadow: isActive ? `0 4px 16px ${CAT_YELLOW}66` : 'none',
                    transition: 'all 0.22s ease',
                  }}>
                    <span style={{ display: 'inline-flex', fontSize: 22 }}>
                      {isCompleted ? <CheckIcon style={{ fontSize: 24 }} /> : step.icon}
                    </span>
                  </div>

                  {/* Labels */}
                  <div style={{ marginTop: 10 }}>
                    <Typography style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      color: isActive ? '#B8860B' : '#9E9E9E',
                    }}>
                      Step {step.id}
                    </Typography>
                    <Typography style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      lineHeight: 1.3,
                      marginTop: 2,
                      color: isActive ? CAT_BLACK : '#616161',
                    }}>
                      {step.title}
                    </Typography>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Active step detail panel */}
          <Collapse in={activeStep !== null} timeout={300} unmountOnExit>
            <div style={{ marginBottom: 40 }}>
              {activeStep !== null && (
                <StepDetailPanel
                  stepId={activeStep}
                  classes={classes}
                  onPrev={() => setActiveStep(prev => (prev && prev > 1 ? prev - 1 : prev))}
                  onNext={() => setActiveStep(prev => (prev && prev < ONBOARDING_STEPS.length ? prev + 1 : prev))}
                  isFirst={activeStep === 1}
                  isLast={activeStep === ONBOARDING_STEPS.length}
                />
              )}
            </div>
          </Collapse>
        </div>
      </div>
    );
}